/**
 * ContractReminderModal — promemoria in-app degli alert contrattuali.
 *
 * Comportamento (scelte utente: a4 / b2 / c1 / d1):
 *  - Si mostra automaticamente ad ogni mount dell'app per super_admin,
 *    hotel_manager e capo_manutentore
 *  - Bloccante (dialog non chiudibile con click esterno)
 *  - Salva in localStorage il fingerprint della lista dismessa. Il modal riappare
 *    SOLO quando la lista cambia (nuovo contratto in scadenza o scaduto)
 *  - Polling ogni 15 minuti per catturare alert live durante la sessione
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, Clock, Bell, ExternalLink } from 'lucide-react';

const LS_KEY = 'hostfix_contract_reminder_dismissed_fp';
const POLL_MS = 15 * 60 * 1000; // 15 minuti
const ALLOWED_ROLES = new Set(['super_admin', 'hotel_manager', 'capo_manutentore', 'sede']);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

const ContractReminderModal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], fingerprint: '', count: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const enabled = user && ALLOWED_ROLES.has(user.role);

  const check = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const { data: res } = await api.get('/reminders/contract-alerts');
      setData(res);
      const seenFp = localStorage.getItem(LS_KEY);
      if (res.count > 0 && res.fingerprint && res.fingerprint !== seenFp) {
        setOpen(true);
      }
    } catch {
      /* silenzioso: se l'endpoint fallisce, il resto dell'app continua */
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Primo check al mount + polling
  useEffect(() => {
    if (!enabled) return undefined;
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, check]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of data.items || []) {
      const key = it.hotel_id || 'unassigned';
      if (!map.has(key)) map.set(key, { hotel_name: it.hotel_name, items: [] });
      map.get(key).items.push(it);
    }
    return Array.from(map.values());
  }, [data]);

  const dismiss = () => {
    if (data.fingerprint) localStorage.setItem(LS_KEY, data.fingerprint);
    setOpen(false);
  };

  const goToContracts = () => {
    if (data.fingerprint) localStorage.setItem(LS_KEY, data.fingerprint);
    setOpen(false);
    navigate('/contratti-manutenzione');
  };

  if (!enabled || loading || data.count === 0) return null;

  const scaduteN = data.items.filter(i => i.severity === 'scaduta').length;
  const inScadenzaN = data.items.length - scaduteN;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="contract-reminder-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Bell className="w-5 h-5 text-amber-600" />
            Promemoria Manutenzioni Contrattuali
          </DialogTitle>
          <DialogDescription>
            Hai{' '}
            {scaduteN > 0 && (
              <span className="font-semibold text-red-700">
                {scaduteN} manutenzion{scaduteN === 1 ? 'e' : 'i'} scadut{scaduteN === 1 ? 'a' : 'e'}
              </span>
            )}
            {scaduteN > 0 && inScadenzaN > 0 && ' e '}
            {inScadenzaN > 0 && (
              <span className="font-semibold text-amber-700">
                {inScadenzaN} in scadenza
              </span>
            )}
            . Verifica lo stato e programma gli interventi.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-4" data-testid="reminder-list">
          {grouped.map((grp) => (
            <div key={grp.hotel_name} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-800 px-4 py-2 font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center justify-between">
                <span>{grp.hotel_name}</span>
                <Badge variant="secondary" className="font-normal">{grp.items.length} avvis{grp.items.length === 1 ? 'o' : 'i'}</Badge>
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {grp.items.map((it) => (
                  <li key={`${it.contract_id}-${it.occurrence_id}`} className="px-4 py-2 flex items-start gap-3 text-sm">
                    {it.severity === 'scaduta' ? (
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100 truncate">
                        {it.supplier_name}
                        <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                          — {it.description}
                        </span>
                      </p>
                      <p className="text-xs mt-0.5">
                        {it.severity === 'scaduta' ? (
                          <span className="text-red-700 dark:text-red-400">
                            Scaduta da {Math.abs(it.days_diff)} giorn{Math.abs(it.days_diff) === 1 ? 'o' : 'i'} ({fmtDate(it.scheduled_date)})
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-400">
                            Tra {it.days_diff} giorn{it.days_diff === 1 ? 'o' : 'i'} ({fmtDate(it.scheduled_date)})
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={dismiss} data-testid="reminder-dismiss">
            Ho capito, ricordamelo dopo
          </Button>
          <Button onClick={goToContracts} className="bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="reminder-go-to-contracts">
            Vai a Manutenzioni <ExternalLink className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractReminderModal;
